/**
 * SocketService — Pusher-based singleton
 *
 * Backend: Laravel 10 + pusher/pusher-php-server + Soketi
 *
 * Channel naming (mirrors channels.php):
 *   KDS  → private-kds.{branchId}
 *   POS  → private-pos.{branchId}
 */

import Pusher from 'pusher-js';
import Cookies from 'js-cookie';

// Enable Pusher debug logging
// Pusher.logToConsole = true;

// ─── Pusher instance (lazy singleton) ─────────────────────────────────────────

let _pusher = null;

function getPusher() {
  if (_pusher) return _pusher;

  _pusher = new Pusher(process.env.VUE_APP_PUSHER_KEY || 'silver-spoon-key', {
    cluster:           'mt1',
    wsHost:            process.env.VUE_APP_WS_HOST || 'localhost',
    wsPort:            Number(process.env.VUE_APP_WS_PORT) || 6001,
    wssPort:           Number(process.env.VUE_APP_WS_PORT) || 6001,
    forceTLS:          process.env.VUE_APP_WS_TLS === 'true',
    enableStats:       false,
    enabledTransports: ['ws'],

    // Heartbeat — detect ว่า Soketi ล่มภายใน ~15 วินาที (default 150 วินาที)
    activityTimeout:   10000,  // 10 วิ ไม่มี message → ping
    pongTimeout:       5000,   // 5 วิ ไม่ได้ pong → ตัดเลย

    // Auth — token อ่านใหม่ทุกครั้ง (ไม่ใช่ค่าตอน init)
    authEndpoint: `${process.env.VUE_APP_API_BASE_URI}/broadcasting/auth`,
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        const token = Cookies.get('token');
        fetch(`${process.env.VUE_APP_API_BASE_URI}/broadcasting/auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${token}`,
          },
          body: `socket_id=${encodeURIComponent(socketId)}&channel_name=${encodeURIComponent(channel.name)}`,
        })
          .then(res => {
            if (!res.ok) throw new Error(`Auth failed: ${res.status}`);
            return res.json();
          })
          .then(data => callback(null, data))
          .catch(err  => callback(err, null));
      },
    }),
  });

  // Throttled connection logging — only log once per state
  let _lastLoggedState = null;
  _pusher.connection.bind('state_change', ({ current }) => {
    if (current === _lastLoggedState) return;
    _lastLoggedState = current;

    if (current === 'connected')        console.info('[Pusher] Connected');
    else if (current === 'disconnected') console.warn('[Pusher] Disconnected — will auto-reconnect');
    else if (current === 'unavailable')  console.warn('[Pusher] Server unavailable');
    else if (current === 'failed')       console.error('[Pusher] Connection failed (unsupported browser?)');
    // 'connecting' ไม่ log เพราะเกิดบ่อยมากตอน reconnect
  });

  return _pusher;
}

// ─── SocketService ────────────────────────────────────────────────────────────

class SocketService {
  constructor() {
    /** @type {Map<string, Pusher.Channel>} channelName → Pusher channel */
    this._channels = new Map();
  }

  /**
   * Subscribe to a Pusher channel.
   * Safe to call multiple times — returns cached channel.
   */
  subscribe(channelName) {
    if (!this._channels.has(channelName)) {
      const ch = getPusher().subscribe(channelName);
      this._channels.set(channelName, ch);
    }
    return this._channels.get(channelName);
  }

  /** Unsubscribe and remove the channel. */
  unsubscribe(channelName) {
    if (this._channels.has(channelName)) {
      getPusher().unsubscribe(channelName);
      this._channels.delete(channelName);
    }
  }

  /**
   * Bind to a server-broadcast event on a channel.
   * @returns {Function} unbind
   */
  on(channelName, event, handler) {
    const ch = this.subscribe(channelName);
    ch.bind(event, handler);
    return () => ch.unbind(event, handler);
  }

  /** True when Pusher global connection is up. */
  isConnected() {
    return getPusher().connection.state === 'connected';
  }

  /**
   * บังคับให้ต่อใหม่ถ้าไม่ได้ต่ออยู่ (no-op ถ้าต่ออยู่แล้ว)
   *
   * มือถือที่ล็อกหน้าจอนาน ๆ เบราว์เซอร์จะแช่แข็ง timer ของ pusher-js ไว้ —
   * พอกลับมา connection อาจค้างที่ 'unavailable'/'disconnected' โดยไม่ retry ต่อเอง
   * เรียกตอน visibilitychange เพื่อปลุกให้ต่อกลับทันที
   */
  ensureConnected() {
    const connection = getPusher().connection;
    if (connection.state !== 'connected' && connection.state !== 'connecting') {
      getPusher().connect();
    }
  }

  /** True when a specific channel is subscribed. */
  isSubscribed(channelName) {
    const ch = this._channels.get(channelName);
    return ch?.subscribed === true;
  }

  /**
   * Bind to connection state changes.
   * @param {Function} handler  (isConnected: boolean, state: string) => void
   * @returns {Function} unbind
   *
   * Pusher states:
   *   connected   → isConnected = true
   *   connecting  → isConnected = false  (reconnecting after drop)
   *   disconnected→ isConnected = false  (manual disconnect)
   *   unavailable → isConnected = false  (retries exhausted)
   *   failed      → isConnected = false  (browser unsupported)
   */
  onConnectionChange(handler) {
    const wrapper = ({ current }) => {
      handler(current === 'connected', current);
    };
    getPusher().connection.bind('state_change', wrapper);
    return () => getPusher().connection.unbind('state_change', wrapper);
  }

  /**
   * Disconnect Pusher entirely (e.g. on logout).
   */
  disconnect() {
    if (_pusher) {
      _pusher.disconnect();
      _pusher = null;
    }
    this._channels.clear();
  }
}

// Singleton
export const socketService = new SocketService();
